import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  Home,
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  FileText,
  DollarSign,
  Shield,
  Target,
  Building2,
  Percent
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulateurRendementLocatif from '../components/MiniSimulateurRendementLocatif';

const GuideInvestissementLocatif = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guideInvestissementLocatif} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-orange-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent leading-tight">
              Guide Investissement Locatif Suisse
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Financement, fiscalité, rendement : tout pour réussir votre investissement immobilier
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 18 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Applicabilité nationale */}
          <div className="p-5 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les règles de financement hypothécaire sont fédérales (FINMA). La fiscalité de l'investissement locatif varie selon les cantons, mais les principes s'appliquent partout en Suisse romande (Vaud, Genève, Neuchâtel, Fribourg, Valais). <strong>Mis à jour : {new Date().toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}</strong>
            </p>
          </div>
          
          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Pourquoi investir dans l'immobilier locatif ?</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              L'investissement locatif reste l'un des placements préférés des Suisses pour se constituer un patrimoine et générer des revenus passifs. Contrairement aux actions ou obligations, l'immobilier offre un actif tangible qui peut être financé par effet de levier (hypothèque) tout en bénéficiant d'avantages fiscaux importants.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              En Suisse, le marché locatif est stable, les loyers encadrés par la loi, et la demande reste forte dans les centres urbains et régions attractives. Que vous cherchiez un complément de revenu, une préparation à la retraite ou une diversification patrimoniale, l'immobilier locatif peut être une excellente option.
            </p>
            
            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou promoteur immobilier. Nos conseils sont objectifs et basés uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Types d'investissements */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-8 h-8 text-yellow-400" />
              <h2 className="text-3xl font-bold text-white">Types d'investissements locatifs</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Il existe plusieurs types d'investissements locatifs en Suisse, chacun avec ses avantages et contraintes.
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/30">
                <h4 className="text-xl font-bold text-white mb-3">1. Appartement en PPE (Propriété par Étages)</h4>
                <p className="text-gray-300 mb-2">
                  <strong>Ticket d'entrée :</strong> CHF 200'000 à 600'000 selon la région
                </p>
                <p className="text-gray-300 mb-3">
                  Vous achetez un appartement dans un immeuble et le louez. C'est l'option la plus accessible pour débuter.
                </p>
                <p className="text-green-400 font-semibold mb-1">✅ Avantages :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4 mb-3">
                  <li>• Capital initial modéré</li>
                  <li>• Gestion simplifiée (la PPE s'occupe de l'entretien commun)</li>
                  <li>• Liquidité plus facile (revente plus rapide qu'un immeuble)</li>
                </ul>
                <div className="p-3 bg-blue-500/10 border-l-2 border-blue-400 rounded-r mb-3">
                  <p className="text-blue-200 text-xs">
                    <strong>💡 PPE :</strong> Propriété Par Étages = copropriété suisse. Vous êtes propriétaire de votre lot + quote-part des parties communes.
                  </p>
                </div>
                <p className="text-red-400 font-semibold mb-1">❌ Inconvénients :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li>• Charges de copropriété (PPE, fonds de rénovation)</li>
                  <li>• Décisions collectives (assemblées, travaux)</li>
                  <li>• Rendement parfois limité (3-4% brut)</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/30">
                <h4 className="text-xl font-bold text-white mb-3">2. Petit immeuble de rapport (2-6 unités)</h4>
                <p className="text-gray-300 mb-2">
                  <strong>Ticket d'entrée :</strong> CHF 800'000 à 2'000'000+
                </p>
                <p className="text-gray-300 mb-3">
                  Vous possédez un immeuble entier avec plusieurs logements. Plus d'autonomie, meilleur rendement potentiel.
                </p>
                <p className="text-green-400 font-semibold mb-1">✅ Avantages :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4 mb-3">
                  <li>• Meilleur rendement (4-6% brut possible)</li>
                  <li>• Contrôle total (décisions, travaux, locataires)</li>
                  <li>• Diversification (plusieurs locataires = risque dilué)</li>
                  <li>• Valeur du terrain incluse</li>
                </ul>
                <p className="text-red-400 font-semibold mb-1">❌ Inconvénients :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li>• Capital important nécessaire</li>
                  <li>• Gestion plus lourde (entretien, locataires multiples)</li>
                  <li>• Moins liquide (revente plus longue)</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-xl border border-red-500/30">
                <h4 className="text-xl font-bold text-white mb-3">3. Parking / Box / Surface commerciale</h4>
                <p className="text-gray-300 mb-2">
                  <strong>Ticket d'entrée :</strong> CHF 20'000 à 150'000
                </p>
                <p className="text-gray-300 mb-3">
                  Investissement de niche, souvent sous-estimé. Gestion ultra-simple, rendement intéressant.
                </p>
                <p className="text-green-400 font-semibold mb-1">✅ Avantages :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4 mb-3">
                  <li>• Gestion quasi-inexistante</li>
                  <li>• Pas d'entretien lourd</li>
                  <li>• Forte demande (pénurie de places en ville)</li>
                  <li>• Peut compléter un bien résidentiel</li>
                </ul>
                <p className="text-red-400 font-semibold mb-1">❌ Inconvénients :</p>
                <ul className="text-gray-300 text-sm space-y-1 ml-4">
                  <li>• Revenus modestes (CHF 100-300/mois)</li>
                  <li>• Pas de plus-value significative</li>
                  <li>• Certaines banques ne financent pas</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Financement */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Financement de votre investissement</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Le financement d'un bien locatif est différent d'une résidence principale. Les banques sont plus strictes car le risque est plus élevé.
            </p>

            <h3 className="text-xl font-bold text-white mb-4">Règles de financement</h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl border border-blue-500/30">
                <h4 className="text-lg font-bold text-white mb-2">Résidence principale</h4>
                <p className="text-gray-300 text-sm mb-2">• Fonds propres : <strong>20% minimum</strong></p>
                <p className="text-gray-300 text-sm mb-2">• LTV max : <strong>80%</strong></p>
                <p className="text-gray-300 text-sm">• Taux : <strong>~1.5-2.5%</strong></p>
              </div>

              <div className="p-6 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl border border-orange-500/30">
                <h4 className="text-lg font-bold text-white mb-2">Investissement locatif</h4>
                <p className="text-gray-300 text-sm mb-2">• Fonds propres : <strong>30-35% minimum</strong></p>
                <p className="text-gray-300 text-sm mb-2">• LTV maximum : <strong>65% à 70% selon la banque</strong></p>
                <p className="text-gray-300 text-sm mb-2">• 75% uniquement dans des cas exceptionnels</p>
                <p className="text-gray-300 text-sm">• Taux : <strong>~2-3% (plus élevé)</strong></p>
              </div>
            </div>

            <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg mb-6">
              <p className="text-gray-200 text-sm mb-3">
                <strong>Capacité financière :</strong> Les banques vérifient que les revenus locatifs couvrent les charges hypothécaires (calculées à 5% théorique) + entretien + amortissement.
              </p>
              <p className="text-gray-300 text-sm mb-3">
                <strong>Formule :</strong> Loyer annuel ≥ (Hypothèque × 5%) + (Valeur × 1% entretien) + Amortissement
              </p>
              <p className="text-gray-200 text-sm">
                <strong>⚠️ Amortissement obligatoire :</strong> Les banques exigent souvent un amortissement obligatoire jusqu'à 56-65% de LTV selon l'établissement.
              </p>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Exemple de financement</h3>

            <div className="p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl border border-yellow-500/30">
              <p className="text-white mb-4"><strong>Achat d'un appartement 3 pièces en Suisse romande</strong></p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-gray-300 text-sm mb-1">Prix d'achat :</p>
                  <p className="text-white font-semibold">CHF 450'000</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm mb-1">Fonds propres (25%) :</p>
                  <p className="text-white font-semibold">CHF 112'500</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm mb-1">Hypothèque :</p>
                  <p className="text-white font-semibold">CHF 337'500</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm mb-1">Loyer mensuel :</p>
                  <p className="text-white font-semibold">CHF 1'650</p>
                </div>
              </div>

              <p className="text-gray-300 text-sm mb-3 border-t border-white/20 pt-3"><strong>Calcul soutenabilité :</strong></p>
              
              <ul className="text-gray-300 text-sm space-y-2 ml-4 mb-4">
                <li>• Revenus locatifs annuels : <strong className="text-white">CHF 19'800</strong></li>
                <li>• Charges théoriques (5% de 337'500) : <strong className="text-red-400">CHF 16'875</strong></li>
                <li>• Entretien (1% de 450'000) : <strong className="text-red-400">CHF 4'500</strong></li>
              </ul>

              <div className="border-t border-white/20 pt-3">
                <p className="text-lg font-bold text-yellow-400">
                  ⚠️ Cash-flow négatif : -CHF 1'575/an
                </p>
                <p className="text-gray-300 text-sm mt-2">
                  Ce bien ne se finance pas "tout seul". Vous devez avoir les revenus personnels pour couvrir la différence.
                </p>
              </div>
            </div>
          </div>

          {/* Simulateur */}
          <MiniSimulateurRendementLocatif />

          {/* Fiscalité */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Percent className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Fiscalité de l'investissement locatif</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              La fiscalité est complexe mais offre des opportunités d'optimisation importantes.
            </p>

            <h3 className="text-xl font-bold text-white mb-4">Revenus locatifs : imposables</h3>

            <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg mb-6">
              <p className="text-gray-200 text-sm mb-2">
                <strong>❌ Mauvaise nouvelle :</strong> Vos loyers sont entièrement imposables comme revenus.
              </p>
              <p className="text-gray-300 text-sm">
                Si vous percevez CHF 20'000/an de loyers, ces CHF 20'000 s'ajoutent à votre revenu imposable (salaire + loyers).
              </p>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Charges déductibles : votre atout fiscal</h3>

            <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg mb-6">
              <p className="text-gray-200 text-sm mb-3">
                <strong>✅ Bonne nouvelle :</strong> Vous pouvez déduire de nombreuses charges pour réduire votre revenu imposable.
              </p>
              
              <p className="text-white font-semibold mb-2">Charges déductibles :</p>
              <ul className="text-gray-300 text-sm space-y-1 ml-4">
                <li>• <strong>Intérêts hypothécaires</strong> (100% déductibles)</li>
                <li>• <strong>Frais d'entretien</strong> (réparations, peinture, rénovations)</li>
                <li>• <strong>Frais de gestion</strong> (régie, comptabilité)</li>
                <li>• <strong>Assurances</strong> (bâtiment, RC propriétaire)</li>
                <li>• <strong>Charges de copropriété</strong> (PPE)</li>
                <li>• <strong>Frais administratifs</strong> (annonces, visites)</li>
              </ul>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Amortissement fiscal : la clé de l'optimisation</h3>

            <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg mb-6">
              <p className="text-gray-200 text-sm mb-3">
                <strong>💡 Point crucial :</strong> Selon les cantons, un amortissement fiscal de 1-2% peut être déduit de vos revenus locatifs.
              </p>
              <p className="text-gray-300 text-sm mb-3">
                <strong>Important :</strong> Vérifier les règles cantonales spécifiques, car elles varient d'un canton à l'autre.
              </p>
              <p className="text-gray-300 text-sm">
                <strong>Exemple indicatif :</strong> Bien à CHF 450'000 → Amortissement déductible potentiel : CHF 4'500 à 9'000/an
              </p>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Exemple fiscal complet</h3>

            <div className="p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
              <p className="text-white mb-4"><strong>Situation : Appartement loué CHF 1'650/mois</strong></p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <p className="text-gray-300 text-sm">Revenus locatifs annuels :</p>
                  <p className="text-white font-semibold">CHF 19'800</p>
                </div>
                <div className="flex justify-between text-red-400">
                  <p className="text-sm">Intérêts hypothécaires (2% de 337'500) :</p>
                  <p className="font-semibold">- CHF 6'750</p>
                </div>
                <div className="flex justify-between text-red-400">
                  <p className="text-sm">Entretien/charges :</p>
                  <p className="font-semibold">- CHF 3'000</p>
                </div>
                <div className="flex justify-between text-red-400">
                  <p className="text-sm">Amortissement (1%) :</p>
                  <p className="font-semibold">- CHF 4'500</p>
                </div>
                <div className="flex justify-between text-red-400">
                  <p className="text-sm">Assurances/frais :</p>
                  <p className="font-semibold">- CHF 1'500</p>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="flex justify-between">
                  <p className="text-lg font-bold text-white">Revenu locatif net imposable :</p>
                  <p className="text-lg font-bold text-green-400">CHF 4'050</p>
                </div>
                <p className="text-gray-300 text-sm mt-2">
                  Au lieu de CHF 19'800, vous n'êtes imposé que sur CHF 4'050 ! → Économie d'impôts significative
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>⚠️ Important :</strong> Ces règles varient selon les cantons. Consultez un fiscaliste pour votre situation spécifique.
              </p>
            </div>
          </div>

          {/* Rendement */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Calcul du rendement locatif</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Il existe plusieurs façons de mesurer le rendement d'un investissement locatif. Voici les plus importantes.
            </p>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30">
                <h4 className="text-xl font-bold text-white mb-3">1. Rendement brut</h4>
                <p className="text-gray-300 mb-3">
                  Le plus simple, mais peu représentatif de la réalité.
                </p>
                <div className="p-4 bg-black/30 rounded-lg mb-3">
                  <p className="text-yellow-400 font-mono text-sm">
                    Rendement brut = (Loyer annuel / Prix d'achat) × 100
                  </p>
                </div>
                <p className="text-gray-300 text-sm">
                  <strong>Exemple :</strong> Loyer CHF 19'800/an, prix CHF 450'000<br/>
                  → Rendement brut = (19'800 / 450'000) × 100 = <strong className="text-green-400">4.4%</strong>
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">2. Rendement net</h4>
                <p className="text-gray-300 mb-3">
                  Plus réaliste : prend en compte les charges.
                </p>
                <div className="p-4 bg-black/30 rounded-lg mb-3">
                  <p className="text-yellow-400 font-mono text-sm">
                    Rendement net = [(Loyer - Charges) / Prix] × 100
                  </p>
                </div>
                <p className="text-gray-300 text-sm">
                  <strong>Exemple :</strong> Loyer CHF 19'800, charges CHF 8'000/an<br/>
                  → Rendement net = (11'800 / 450'000) × 100 = <strong className="text-blue-400">2.6%</strong>
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">3. Cash-on-Cash Return (le plus pertinent)</h4>
                <p className="text-gray-300 mb-3">
                  Mesure le rendement sur votre argent investi (fonds propres), pas sur la valeur totale.
                </p>
                <div className="p-4 bg-black/30 rounded-lg mb-3">
                  <p className="text-yellow-400 font-mono text-sm">
                    Cash-on-Cash = (Cash-flow annuel / Fonds propres) × 100
                  </p>
                </div>
                <p className="text-gray-300 text-sm mb-2">
                  <strong>Exemple :</strong> Cash-flow négatif -CHF 1'575/an, fonds propres CHF 112'500<br/>
                  → Cash-on-Cash = (-1'575 / 112'500) × 100 = <strong className="text-red-400">-1.4%</strong>
                </p>
                <p className="text-gray-300 text-sm">
                  Ce bien coûte de l'argent chaque année, mais vous misez sur la plus-value à long terme.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>💡 Objectif réaliste :</strong> Un bon investissement locatif vise un rendement net de 3-5%. Objectif idéal : cash-on-cash positif, mais <strong>en Suisse en 2025 il est fréquent d'avoir un cash-flow légèrement négatif les premières années</strong>, compensé par la plus-value à long terme.
              </p>
            </div>
          </div>

          {/* Stratégies d'optimisation */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-orange-400" />
              <h2 className="text-3xl font-bold text-white">5 stratégies pour maximiser votre rendement</h2>
            </div>

            <div className="space-y-4">
              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">1️⃣ Acheter dans les zones en développement</h4>
                <p className="text-gray-300 text-sm">
                  Évitez les centres-villes saturés. Ciblez les communes en croissance (nouveaux transports, entreprises qui s'installent). Plus-value potentielle élevée.
                </p>
              </div>

              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">2️⃣ Rénover avant de louer</h4>
                <p className="text-gray-300 text-sm">
                  Un bien rénové se loue plus cher et plus vite. Investissez dans cuisine, salle de bain, peinture. Ces travaux sont déductibles fiscalement.
                </p>
              </div>

              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">3️⃣ Financer au maximum (effet de levier)</h4>
                <p className="text-gray-300 text-sm">
                  Plus votre hypothèque est élevée, plus vos intérêts déductibles sont importants. Si vous avez les revenus pour supporter les charges, privilégiez un LTV proche du maximum autorisé (65-70%, rarement 75%).
                </p>
              </div>

              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">4️⃣ Optimiser la fiscalité</h4>
                <p className="text-gray-300 text-sm">
                  Déduisez TOUTES les charges possibles : intérêts, entretien, amortissement, frais de déplacement pour visites, frais de comptabilité, etc. Tenez une comptabilité rigoureuse.
                </p>
              </div>

              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">5️⃣ Diversifier (plusieurs petits biens plutôt qu'un gros)</h4>
                <p className="text-gray-300 text-sm">
                  3 studios valent mieux qu'un appartement 4 pièces au même prix. Si un locataire part, vous gardez 2/3 de vos revenus. Risque dilué.
                </p>
              </div>
            </div>
          </div>

          {/* Risques et pièges */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">7 pièges à éviter</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Sous-estimer les charges réelles",
                  desc: "Entretien, vacance locative, impayés : prévoyez 20-30% de marge sur vos calculs."
                },
                {
                  title: "Négliger la vacance locative",
                  desc: "Un bien peut rester vide 1-3 mois entre deux locataires. Cela impacte votre cash-flow."
                },
                {
                  title: "Acheter trop cher pour le loyer du marché",
                  desc: "Un prix d'achat élevé ne se traduit pas forcément par un loyer élevé (loyers encadrés en Suisse)."
                },
                {
                  title: "Ignorer l'état du bien",
                  desc: "Un bien nécessitant de gros travaux (chauffage, toiture) peut ruiner votre rentabilité."
                },
                {
                  title: "Ne pas prévoir de fonds de roulement",
                  desc: "Gardez CHF 10'000-20'000 de trésorerie pour les imprévus (réparations urgentes)."
                },
                {
                  title: "Oublier la gestion locative",
                  desc: "Gérer des locataires prend du temps (visites, état des lieux, entretien). Budgétez une régie si besoin (8-10% du loyer)."
                },
                {
                  title: "Miser uniquement sur la plus-value",
                  desc: "En Suisse, l'immobilier progresse lentement. Un investissement doit être rentable dès le départ (cash-flow positif ou neutre)."
                }
              ].map((piege, index) => (
                <div key={index} className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                  <h4 className="font-bold text-white mb-2">❌ {piege.title}</h4>
                  <p className="text-gray-300 text-sm">{piege.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Quel rendement minimum viser pour un investissement locatif ?",
                  a: "Visez au minimum 3-4% de rendement net après charges. En dessous, l'investissement n'est probablement pas intéressant. Privilégiez un cash-on-cash positif dès le départ."
                },
                {
                  q: "Puis-je utiliser mon 2ème pilier pour financer un bien locatif ?",
                  a: "Non, le retrait anticipé du 2ème pilier est uniquement autorisé pour l'achat de votre résidence principale. Pas pour un investissement locatif."
                },
                {
                  q: "Faut-il créer une société pour gérer un bien locatif ?",
                  a: "Pour 1-2 biens, ce n'est généralement pas nécessaire. Au-delà de 3-4 biens ou si vous êtes très actif, une structure juridique (Sàrl) peut offrir des avantages fiscaux. Consultez un fiscaliste."
                },
                {
                  q: "Comment fixer le loyer d'un bien ?",
                  a: "Le loyer est encadré par la loi suisse (basé sur le taux hypothécaire de référence + rendement du capital investi). Consultez les loyers du marché sur Homegate, Immoscout24. Ne surestimez pas !"
                },
                {
                  q: "Que se passe-t-il en cas d'impayés de loyer ?",
                  a: "La procédure d'expulsion en Suisse peut prendre 6-12 mois. Souscrivez une assurance protection juridique et prévoyez une réserve financière pour couvrir cette période."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-yellow-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Conclusion + CTA final */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              L'investissement locatif en Suisse peut être très intéressant, mais il demande une analyse rigoureuse, une bonne gestion et de la patience. Les rendements bruts élevés (>5%) sont rares dans les grandes villes, mais une stratégie bien pensée peut générer des revenus stables et une plus-value à long terme.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">L'essentiel :</strong> Ne vous précipitez pas. Analysez plusieurs biens, faites des simulations détaillées, et entourez-vous de professionnels (courtier, notaire, fiscaliste) pour sécuriser votre investissement.
            </p>

            <div className="p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl border border-yellow-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Analyser la rentabilité de votre projet d'investissement
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Négocier votre financement hypothécaire au meilleur taux
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Optimiser la fiscalité de vos revenus locatifs
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Structurer votre patrimoine immobilier
                </li>
              </ul>

              <div className="text-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all transform hover:scale-105 text-lg"
                >
                  Réserver une analyse gratuite
                  <ArrowRight className="w-6 h-6" />
                </Link>
                <p className="text-gray-400 text-sm mt-3">
                  🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default GuideInvestissementLocatif;
